import { type TravelBookingReport } from '@utazzo/shared';
import { UzAlert, UzSelect, UzSkeleton } from '@utazzo/ui';
import {
  Ban,
  BedDouble,
  Clock,
  Globe2,
  IndianRupee,
  Percent,
  Plane,
  ReceiptText,
  TrendingUp,
  Undo2,
  Users,
  Wallet,
} from 'lucide-react';
import React, { useState } from 'react';

import { dateLabel, money } from '../booking-format';
import { useTravelBookingsReport } from '../use-travel-bookings';

import { PageShell } from '@/components/page-shell';
import { KpiCard, KpiGrid } from '@/features/reports/components/KpiCard';
import { MonthYearFilter } from '@/features/reports/components/MonthYearFilter';
import { LegacyTable, ReportSection } from '@/features/reports/components/ReportSection';

type ProductFilter = '' | 'FLIGHT' | 'HOTEL';

/**
 * Ticketing > Reports.
 *
 * One month of website bookings, the way the owner asks about it: money in,
 * money out, what sold and where to. Counted by payment date in IST. The
 * same month / year selector as Lead reports, plus a product switch, so the
 * numbers can be read for flights and hotels apart.
 */
export const TicketingReportsPage = (): React.JSX.Element => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [product, setProduct] = useState<ProductFilter>('');
  const report = useTravelBookingsReport({
    month,
    year,
    ...(product ? { product } : {}),
  });
  const r = report.data;
  const c = r?.currencyCode ?? 'INR';

  return (
    <PageShell
      title="Reports"
      description="Website flight and hotel bookings by month: sales, cost, margin, refunds, the checkout funnel, holds, and what sold where."
      actions={
        // Stacked on a phone, one row from `sm`. The month / year pair is a
        // non-wrapping row of its own inside MonthYearFilter, which is what
        // pushed the whole page sideways on a 390px screen; here it is made
        // to wrap through the parent selector.
        <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-end [&>div]:flex-wrap">
          <UzSelect<ProductFilter>
            value={product}
            onValueChange={setProduct}
            containerClassName="w-full sm:w-40"
            options={[
              { value: '', label: 'Flights + hotels' },
              { value: 'FLIGHT', label: 'Flights only' },
              { value: 'HOTEL', label: 'Hotels only' },
            ]}
          />
          <MonthYearFilter
            month={month}
            year={year}
            onChange={(m, y) => {
              setMonth(m);
              setYear(y);
            }}
          />
        </div>
      }
    >
      {report.isError ? (
        <UzAlert
          tone="destructive"
          title="Could not load the report"
          description="The server returned an error. Try another month or refresh."
        />
      ) : !r ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <UzSkeleton key={i} className="h-20" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <KpiGrid>
            <KpiCard
              label="Paid bookings"
              value={r.totals.paid}
              icon={ReceiptText}
              tone="primary"
              hint={`${r.totals.confirmed} confirmed · ${r.totals.travellers} travellers`}
            />
            <KpiCard
              label="Gross sales"
              value={money(r.totals.grossSalesPaise, c)}
              icon={IndianRupee}
              tone="success"
              hint={`avg ${money(r.totals.averageBookingPaise, c)} per booking`}
            />
            <KpiCard
              label="Markup"
              value={money(r.totals.markupPaise, c)}
              icon={TrendingUp}
              hint={`+ convenience ${money(r.totals.convenienceFeePaise, c)}`}
            />
            <KpiCard
              label="Net margin"
              value={r.totals.netMarginPaise === null ? '-' : money(r.totals.netMarginPaise, c)}
              icon={Wallet}
              tone={
                r.totals.netMarginPaise !== null && BigInt(r.totals.netMarginPaise) < 0n
                  ? 'danger'
                  : 'default'
              }
              hint={
                r.totals.supplierCostIncomplete
                  ? 'some supplier costs are in another currency'
                  : `after supplier cost ${money(r.totals.supplierCostPaise, c)}, GST, refunds`
              }
            />
            <KpiCard
              label="Refunded"
              value={money(r.totals.refundedPaise, c)}
              icon={Undo2}
              tone={BigInt(r.totals.refundedPaise) > 0n ? 'warning' : 'default'}
              hint={`${r.totals.refunded} booking${r.totals.refunded === 1 ? '' : 's'}`}
            />
            <KpiCard
              label="Cancelled"
              value={r.totals.cancelled}
              icon={Ban}
              tone={r.totals.cancelled > 0 ? 'warning' : 'default'}
              hint={`${r.totals.failed} failed after payment`}
            />
            <KpiCard
              label="On hold right now"
              value={r.totals.onHoldNow}
              icon={Clock}
              tone={r.totals.onHoldNow > 0 ? 'danger' : 'default'}
              to="/ticketing/action"
              hint="across all months"
            />
            <KpiCard
              label="GST collected"
              value={money(r.totals.gstPaise, c)}
              icon={Percent}
              hint={`taxes to suppliers ${money(r.totals.taxPaise, c)}`}
            />
          </KpiGrid>

          {r.totals.supplierCostIncomplete && (
            <UzAlert
              tone="warning"
              title="Margin is not shown for this month"
              description="At least one booking was billed by the supplier in a currency other than the sell currency, and no exchange rate was recorded at booking time. Supplier cost and margin leave those out rather than guess."
            />
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ReportSection title="By product">
              <LegacyTable
                headers={[
                  'Product',
                  'Paid',
                  'Confirmed',
                  'Cancelled',
                  'Gross sales',
                  'Markup',
                  'Refunded',
                ]}
                rows={r.byProduct.map((p) => [
                  p.product === 'FLIGHT' ? 'Flights' : 'Hotels',
                  p.paid,
                  p.confirmed,
                  p.cancelled,
                  money(p.grossSalesPaise, c),
                  money(p.markupPaise, c),
                  money(p.refundedPaise, c),
                ])}
                emptyText="No paid bookings this month."
              />
            </ReportSection>

            <ReportSection title="Checkout funnel (sessions started this month)">
              <Funnel funnel={r.funnel} />
            </ReportSection>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ReportSection title="Holds (wallet refused, admin decided)">
              <LegacyTable
                headers={['Measure', 'Value']}
                rows={[
                  ['Put on hold', r.holds.placed],
                  ['Booked by an admin', r.holds.bookedByAdmin],
                  ['Refunded instead', r.holds.refunded],
                  ['Still waiting', r.holds.stillWaiting],
                  [
                    'Average time to a decision',
                    r.holds.averageMinutesToResolve === null
                      ? '-'
                      : minutesLabel(r.holds.averageMinutesToResolve),
                  ],
                  ['Price differences absorbed', money(r.holds.absorbedDeltaPaise, c)],
                ]}
              />
            </ReportSection>

            <ReportSection title="Shape of the month">
              <LegacyTable
                headers={['Measure', 'Value']}
                rows={[
                  [
                    'Average lead time (booking to travel)',
                    r.averageLeadTimeDays === null ? '-' : `${r.averageLeadTimeDays} days`,
                  ],
                  [
                    'Average nights per stay',
                    r.averageNights === null ? '-' : `${r.averageNights}`,
                  ],
                  ['Low-cost carriers', r.lccShare === null ? '-' : `${r.lccShare}%`],
                  [
                    'Refundable fares / rates',
                    r.refundableShare === null ? '-' : `${r.refundableShare}%`,
                  ],
                  [
                    'Cabin mix',
                    r.cabinMix.length
                      ? r.cabinMix.map((m) => `${m.label} ${m.count}`).join(', ')
                      : '-',
                  ],
                ]}
              />
            </ReportSection>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Ranked title="Top routes" icon={Plane} rows={r.topRoutes} currency={c} />
            <Ranked title="Top airlines" icon={Plane} rows={r.topAirlines} currency={c} />
            <Ranked title="Top hotels" icon={BedDouble} rows={r.topHotels} currency={c} />
            <Ranked
              title="Top countries (hotels)"
              icon={Globe2}
              rows={r.topCountries}
              currency={c}
            />
            <Ranked
              title="Top destinations (flights)"
              icon={Globe2}
              rows={r.topDestinations}
              currency={c}
            />
            <ReportSection title="Bookings by day">
              <LegacyTable
                headers={['Day', 'Paid', 'Sales']}
                rows={r.byDay.map((d) => [dateLabel(d.date), d.paid, money(d.salesPaise, c)])}
                emptyText="No paid bookings this month."
              />
            </ReportSection>
          </div>
        </div>
      )}
    </PageShell>
  );
};

function Ranked({
  title,
  icon: Icon,
  rows,
  currency,
}: {
  title: string;
  icon: typeof Plane;
  rows: TravelBookingReport['topRoutes'];
  currency: string;
}): React.JSX.Element {
  return (
    <ReportSection title={title} actions={<Icon className="h-4 w-4 text-zinc-400" aria-hidden />}>
      <LegacyTable
        headers={['#', 'Name', 'Bookings', 'Sales']}
        rows={rows.map((row, i) => [i + 1, row.label, row.count, money(row.salesPaise, currency)])}
        emptyText="Nothing booked this month."
      />
    </ReportSection>
  );
}

/** Six steps, each with its count and the share of the step before it. */
function Funnel({ funnel }: { funnel: TravelBookingReport['funnel'] }): React.JSX.Element {
  const steps: Array<{ label: string; value: number }> = [
    { label: 'Search opened', value: funnel.started },
    { label: 'Fare / rate priced', value: funnel.priced },
    { label: 'Details entered', value: funnel.detailsEntered },
    { label: 'Payment started', value: funnel.paymentStarted },
    { label: 'Paid', value: funnel.paid },
    { label: 'Booked with supplier', value: funnel.confirmed },
  ];
  const top = Math.max(1, steps[0]?.value ?? 1);
  return (
    <div className="flex flex-col gap-2">
      {steps.map((s, i) => {
        const prev = i === 0 ? s.value : (steps[i - 1]?.value ?? 0);
        const share = prev > 0 ? Math.round((s.value / prev) * 100) : 0;
        return (
          <div key={s.label} className="flex items-center gap-2 text-xs sm:gap-3 sm:text-sm">
            <div className="w-28 shrink-0 leading-tight text-zinc-700 sm:w-40">{s.label}</div>
            <div className="h-5 flex-1 overflow-hidden rounded bg-zinc-100">
              <div
                className="h-full rounded bg-sky-500/70"
                style={{ width: `${Math.max(2, Math.round((s.value / top) * 100))}%` }}
              />
            </div>
            <div className="w-16 shrink-0 text-right tabular-nums sm:w-24">
              <span className="font-medium text-zinc-900">{s.value}</span>
              {i > 0 && <span className="ml-1 text-xs text-zinc-500">{share}%</span>}
            </div>
          </div>
        );
      })}
      <p className="mt-1 flex items-center gap-1 text-[11px] text-zinc-500">
        <Users className="h-3 w-3" aria-hidden /> Each percentage is of the step before it.
      </p>
    </div>
  );
}

function minutesLabel(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h >= 48 ? `${Math.round(h / 24)} days` : `${h}h ${m}m`;
}
